import * as React from 'react'
import map from 'lodash/map'
import filter from 'lodash/filter'
import partition from 'lodash/partition'
import every from 'lodash/every'
import {isBefore, isAfter} from 'date-fns'
import {Popover, PopoverDisclosure, PopoverProvider} from '@ariakit/react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faQuestionCircle, faPlusCircle} from '@fortawesome/free-solid-svg-icons'
import {Button} from '@frontrow/console/src/components/button'
import {type LicenseT} from '@frontrow/console/src/entities/models/license'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'

import {type MultipleSubjectLicensesT} from '@frontrow/console/src/components/common/school-licenses-list'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import LicenseRow from '@frontrow/console/src/components/common/school-licenses-list/license-row'
import NewLicenseRow from '@frontrow/console/src/components/common/school-licenses-list/new-license-row'
import ExpiredLicensesButtonModal from '@frontrow/console/src/components/common/school-licenses-list/expired-licenses-button-modal'
import SchoolCourseSubjectsByLicensingToggle from '@frontrow/console/src/components/common/school-licenses-list/school-course-subjects-by-licensing-toggle'
import {
  schoolLicensesListContainer,
  licenseDisclaimerText,
  licenseRowsContainer,
  schoolDetailContainer,
  schoolNameText,
  schoolActionsContainer,
  schoolCourseSubjectsByLicensingToggleLabel
} from './school-licenses-list.module.scss'

type Props = {
  schoolName: string
  licenses: Array<LicenseT>
  shouldShowNewLicense: boolean
  showNewLicenseConstraint: boolean
  duplicatedLicense: LicenseT | undefined | null
  isLoading: boolean
  error: string | undefined | null
  showNewLicense: (license?: LicenseT | null) => void
  hideNewLicense: () => void
  addNewLicenses: (licenseData: MultipleSubjectLicensesT) => void
  removeLicense: (licenseId: number) => void
  courseSubjectsByLicensing: boolean
  setCourseSubjectsByLicensing?: (courseSubjectsByLicensing: boolean) => void
  enableModifications: boolean
}

export default function SchoolLicensesList(props: Props): React.ReactElement {
  const {
    schoolName,
    licenses,
    shouldShowNewLicense,
    showNewLicenseConstraint,
    isLoading,
    error,
    duplicatedLicense,
    showNewLicense,
    hideNewLicense,
    addNewLicenses,
    removeLicense,
    courseSubjectsByLicensing,
    setCourseSubjectsByLicensing,
    enableModifications
  } = props

  const now = new Date()

  const [expiredLicenses, unexpiredLicenses] = partition(licenses, license =>
    isBefore(license.expiresAt, now)
  )

  const noLicensesText =
    unexpiredLicenses.length === 0 ? (
      <div className={licenseDisclaimerText}>No current licenses</div>
    ) : null

  const licensesNode =
    licenses.length > 0 ? (
      <div className={licenseRowsContainer}>
        {map(unexpiredLicenses, (license, i: number) => (
          <LicenseRow
            key={i}
            schoolName={schoolName}
            license={license}
            removeLicense={removeLicense}
            duplicateLicense={showNewLicense}
            enableModifications={enableModifications}
          />
        ))}
      </div>
    ) : null

  const errorNode = error !== undefined && error !== null ? <ErrorAlert>{error}</ErrorAlert> : null

  const newLicense = shouldShowNewLicense ? (
    <NewLicenseRow
      license={duplicatedLicense}
      schoolName={schoolName}
      addLicenses={addNewLicenses}
      onDelete={hideNewLicense}
    />
  ) : null

  const newLicenseConstraint = showNewLicenseConstraint ? (
    <div className={licenseDisclaimerText}>
      Save or delete existing new license before adding a new one
    </div>
  ) : null

  const licensingToggle =
    setCourseSubjectsByLicensing !== null &&
    setCourseSubjectsByLicensing !== undefined &&
    canSetCourseSubjectsByLicensing(licenses) ? (
      <PopoverProvider placement="bottom">
        <PopoverDisclosure>
          <p className={schoolCourseSubjectsByLicensingToggleLabel}>
            Set New Course Subjects by Licensing <FontAwesomeIcon icon={faQuestionCircle} />
          </p>
        </PopoverDisclosure>
        <Popover
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            padding: '12px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '300px'
          }}
        >
          When this switch is turned ON, new courses at this school will only have access to the
          Premium subject (Math or ELA) by default. This will not affect existing courses or courses
          created by Clever/Classlink.
        </Popover>
        <SchoolCourseSubjectsByLicensingToggle
          courseSubjectsByLicensing={courseSubjectsByLicensing}
          setCourseSubjectsByLicensing={() =>
            setCourseSubjectsByLicensing(!courseSubjectsByLicensing)
          }
        />
      </PopoverProvider>
    ) : null

  const body = isLoading ? (
    <SpinnerOverlay />
  ) : (
    <React.Fragment>
      <div className={schoolDetailContainer}>
        <div className={schoolNameText}>{schoolName}</div>
        <div className={schoolActionsContainer}>
          {noLicensesText}
          <Button style="link" onClick={() => showNewLicense(null)} disabled={!enableModifications}>
            <FontAwesomeIcon icon={faPlusCircle} size="lg" />
            Add new license
          </Button>
          <ExpiredLicensesButtonModal expiredLicenses={expiredLicenses} schoolName={schoolName} />
        </div>
      </div>
      {newLicense}
      {newLicenseConstraint}
      {licensesNode}
      {licensingToggle}
    </React.Fragment>
  )

  return (
    <div className={schoolLicensesListContainer}>
      {errorNode}
      {body}
    </div>
  )
}

// Only allow the setCourseSubjectsByLicensing field to be toggled when the school has premium math or ELA but not all subjects
function canSetCourseSubjectsByLicensing(licenses: Array<LicenseT>): boolean {
  const now = new Date()
  const validLicenses = filter(
    licenses,
    ({startsAt, expiresAt}) => isAfter(now, startsAt) && isBefore(now, expiresAt)
  )
  return (
    validLicenses.length > 0 &&
    (every(validLicenses, ({subject}) => subject === 'math') ||
      every(validLicenses, ({subject}) => subject === 'ela'))
  )
}
